import { createSlice } from '@reduxjs/toolkit';
import { adjustStock, fetchAdjustBuckets, fetchStock, fetchTransferBuckets, transferStock } from './stockThunks';
import { ALL_FILTER, EMPTY_STOCK_TOTALS, STOCK_GROUP_BY, STOCK_VIEW } from './stockConstants';
import { selectWarehouse } from '../warehouseContext/warehouseContextSlice';

const initialState = {
  /** Current page of balances. Their shape depends on `groupedBy`. */
  rows: [],
  totalCount: 0,
  /** Over the whole filter, not over the page: the server adds them up. */
  totals: EMPTY_STOCK_TOTALS,
  /**
   * How the server grouped the rows it sent: `'sku'`, `'sku_lot'` or `null`
   * for the detail. Starts on the grouping of the view the listing opens with,
   * so the table does not draw the detail's columns while the first page loads.
   */
  groupedBy: STOCK_GROUP_BY.SKU,
  loading: false,
  error: null,
  /** The request whose answer may still write. See `fetchStock.fulfilled`. */
  requestId: null,
  filters: {
    company: ALL_FILTER,
    state: ALL_FILTER,
    search: '',
    includeZero: false,
    view: STOCK_VIEW.CONSOLIDATED,
    page: 1,
    rowsPerPage: 20,
  },

  /**
   * The "Guardar / mover" dialog. Apart from everything above, which drives
   * the table: asking where a product is must not blank the list behind it.
   */
  transfer: {
    /** The product whose origins are loaded, so a late answer cannot mix them. */
    productId: null,
    /** Where that product can leave from: available stock sitting in a location. */
    buckets: [],
    loadingBuckets: false,
    saving: false,
  },

  /** The "Ajustar" dialog: same shape, same reason to live apart from the table. */
  adjust: {
    productId: null,
    /** Where that product sits available, the unplaced included. */
    buckets: [],
    loadingBuckets: false,
    saving: false,
  },
};

const stockSlice = createSlice({
  name: 'stock',
  initialState,
  reducers: {
    /**
     * Merges a patch into the filters. Anything but a page change goes back to
     * the first page: staying on page 4 of a result that now has one is an
     * empty screen that looks like a failure.
     */
    setStockFilters: (state, action) => {
      state.filters = { ...state.filters, page: 1, ...action.payload };
    },
    /** Closing the dialog: the next opening must not start on these origins. */
    clearStockTransfer: (state) => {
      state.transfer = initialState.transfer;
    },
    clearStockAdjust: (state) => {
      state.adjust = initialState.adjust;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStock.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchStock.fulfilled, (state, action) => {
        // Answers can land out of order while a filter is being changed. Only
        // the latest request may write, or the table ends up showing one
        // grouping under the switch of the other.
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.rows = action.payload.results || [];
        state.totalCount = action.payload.count || 0;
        state.totals = { ...EMPTY_STOCK_TOTALS, ...(action.payload.totals || {}) };
        state.groupedBy = action.payload.grouped_by ?? null;
      })
      .addCase(fetchStock.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload;
        // Rows of the previous filter under the error of this one would read
        // as the answer to it.
        state.rows = [];
        state.totalCount = 0;
        state.totals = EMPTY_STOCK_TOTALS;
      })

      // Store / move
      .addCase(fetchTransferBuckets.pending, (state, action) => {
        state.transfer.productId = action.meta.arg.productId;
        state.transfer.buckets = [];
        state.transfer.loadingBuckets = true;
      })
      .addCase(fetchTransferBuckets.fulfilled, (state, action) => {
        // The product can be changed while its origins are on their way.
        if (action.payload.productId !== state.transfer.productId) return;
        state.transfer.loadingBuckets = false;
        state.transfer.buckets = action.payload.buckets;
      })
      .addCase(fetchTransferBuckets.rejected, (state, action) => {
        if (action.meta.arg.productId !== state.transfer.productId) return;
        state.transfer.loadingBuckets = false;
      })
      // The failure is not stored: the dialog that asked is still open and
      // shows it there, while `error` would replace the table with it.
      .addCase(transferStock.pending, (state) => {
        state.transfer.saving = true;
      })
      .addCase(transferStock.fulfilled, (state) => {
        state.transfer.saving = false;
      })
      .addCase(transferStock.rejected, (state) => {
        state.transfer.saving = false;
      })

      // Adjust
      .addCase(fetchAdjustBuckets.pending, (state, action) => {
        state.adjust.productId = action.meta.arg.productId;
        state.adjust.buckets = [];
        state.adjust.loadingBuckets = true;
      })
      .addCase(fetchAdjustBuckets.fulfilled, (state, action) => {
        if (action.payload.productId !== state.adjust.productId) return;
        state.adjust.loadingBuckets = false;
        state.adjust.buckets = action.payload.buckets;
      })
      .addCase(fetchAdjustBuckets.rejected, (state, action) => {
        if (action.meta.arg.productId !== state.adjust.productId) return;
        state.adjust.loadingBuckets = false;
      })
      .addCase(adjustStock.pending, (state) => {
        state.adjust.saving = true;
      })
      .addCase(adjustStock.fulfilled, (state) => {
        state.adjust.saving = false;
      })
      .addCase(adjustStock.rejected, (state) => {
        state.adjust.saving = false;
      })

      // Another warehouse is another set of clients: one that does not operate
      // there stops making sense as a filter.
      .addCase(selectWarehouse, (state) => {
        state.filters.company = ALL_FILTER;
        state.filters.page = 1;
      });
  },
});

export const { setStockFilters, clearStockTransfer, clearStockAdjust } = stockSlice.actions;

export default stockSlice.reducer;
